import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { Role } from "@components/RoleSelection";
import { useRoleSelectionQuery } from "@api/useRoleSelectionQuery";

import Routes from "@utils/routes";
import { tracking } from "@utils/tracking";

import PermissionsProvider, {
  PermissionsContext,
  PermissionsContextType,
} from "./PermissionsContext";
import {
  NavRouteProps,
  RouteWithSub,
  getNavigationRoutes,
} from "./Navigation.config";
import { SideNav, SideNavContext, SideNavItem } from "./SideNav";

/**
 * This component will render the navigation for the application. It will accept
 * permissions and feature toggles as props to determine what navigation items
 * to render.
 */

type CurrentUser = {
  id: string;
  fullName?: string;
  organization?: string;
  role?: string;
};

type NavigationProps = {
  /**
   * The tenant for the current user.
   */
  tenant: {
    slug: string;
    name: string;
    logo: {
      url: string;
    };
  };
  /**
   * The client for the current user.
   */
  client?: {
    id: string;
    first_names?: string;
    last_name?: string;
    prospect?: boolean;
  };
  /**
   * The current wealth manager.
   */
  wealth_manager?: {
    id: string;
  };
  /**
   * The current user.
   */
  currentUser: CurrentUser;
  /**
   * User type
   */
  user_type?: string;
  /**
   * Joint scenario
   */
  jointScenario: { id: string };
  /**
   * Creating joint scenario for unique path. (joint_scenarios/new)
   */
  creatingJointScenario?: boolean;
  /**
   * The permission set for the current user.
   */
  permissions: PermissionsContextType;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: false,
      useErrorBoundary: true,
    },
  },
});

const NavigationContext = (
  props: NavigationProps & {
    permissions: string[];
    featureTogglePermissions: string[];
  }
) => {
  return (
    <QueryClientProvider client={queryClient}>
      <PermissionsProvider
        permissions={props.permissions}
        featureTogglePermissions={props.featureTogglePermissions}
      >
        <PermissionsContext.Consumer>
          {(permissions) => (
            <NavigationWrapper {...props} permissions={permissions} />
          )}
        </PermissionsContext.Consumer>
      </PermissionsProvider>
    </QueryClientProvider>
  );
};

const NavigationWrapper = (props: NavigationProps) => {
  const routes = getNavigationRoutes({
    currentUserId: props.currentUser.id,
    userType: props.user_type as NavRouteProps["userType"],
    tenantSlug: props.tenant.slug,
    clientId: props.client?.id,
    clientName: props.client
      ? `${props.client?.first_names} ${props.client?.last_name}`
      : "",
    isProspect: props.client?.prospect,
    wealthManagerId: props.wealth_manager?.id,
    jointScenarioId: props.jointScenario?.id,
    isNewJointScenario: props.creatingJointScenario,
    permissions: props.permissions,
  });
  const userRoles = useRoleSelectionQuery({
    tenantSlug: props.tenant.slug,
    initialData: undefined,
    queryKey: `user-roles-${props.tenant.slug}`,
  });

  const selectRole = async (role: Role) => {
    const result = await userRoles.actions.update({
      id: props.currentUser.id,
      user_role_id: role.role.id,
      user_role_type: role.role.type,
    });

    if (result.redirectPath) {
      tracking.register(result.tracking.register);
      tracking.identify(result.tracking.identify);
      window.location.href = result.redirectPath;
    }
  };
  return (
    <NavigationRouter
      {...props}
      roles={userRoles.data ?? []}
      routes={routes ?? []}
      onRoleSelected={selectRole}
    />
  );
};

/*
  Recursive function to pull the matching path, and parent tree from the available routes.
  Steps:
  - if path doesn't match current route (or current route doesn't have a path), test for sub routes
  - if path does match current route, return array with current route
  - if child search returns false, return false, otherwise push current route to array and return array
*/

const getActiveRoute = (
  routes: RouteWithSub[],
  path: string
): RouteWithSub[] => {
  for (let route of routes) {
    if (route.subNav) {
      const subRoute = getActiveRoute(route.subNav, path);
      if (subRoute.length > 0) {
        route.isActive = true;
        return [route, ...subRoute];
      }
    }
    // Routes can declare extra paths that should activate them (e.g. a Profile
    // tab that also owns the edit form at a different URL).
    if (route.matchPaths?.includes(path)) {
      route.isActive = true;
      return [route];
    }
    if (route.path) {
      if (path == route.path) {
        route.isActive = true;
        return [route];
      } else {
        // MAGIC: This is an exception for when viewing a specific scenario; needs reworking!
        const splitPath = path.split("/").splice(1);
        const noteworthy =
          splitPath.length > 1 && splitPath[splitPath.length - 2];
        if (noteworthy == "scenarios") {
          if (path.includes(route.path)) {
            route.isActive = true;
            return [route];
          }
        }
      }
    }
  }
  return [];
};

const SIDENAV_PUSH_CLASS = "wb-sidenav-push";

// Resolve the destination a nav item should link to. Top-level sections have no
// path of their own, so fall through to their first navigable child.
const resolveHref = (route: RouteWithSub): string => {
  if (route.path) return route.path;
  if (route.subNav && route.subNav.length > 0) return resolveHref(route.subNav[0]);
  return "#";
};

type NavigationRouterProps = NavigationProps & {
  routes: RouteWithSub[];
  roles: Role[];
  onRoleSelected: (role: Role) => void;
};

const NavigationRouter = ({
  tenant,
  wealth_manager,
  user_type,
  roles,
  routes,
  onRoleSelected,
  currentUser,
}: NavigationRouterProps) => {
  const helpPath = Routes.helpPath();
  const settingsPath =
    user_type == "WealthManager"
      ? Routes.settingsPath({
          tenantSlug: tenant.slug,
          wealthManagerId: wealth_manager?.id,
        })
      : undefined;

  const activeRouteStack = React.useMemo(
    () => getActiveRoute(routes, window.location.pathname),
    [routes]
  );

  const sideBarItems = React.useMemo<SideNavItem[]>(
    () =>
      routes.map((route) => ({
        id: route.id,
        label: route.label,
        icon: route.icon,
        href: resolveHref(route),
        isActive:
          activeRouteStack.length > 0 && activeRouteStack[0].id == route.id,
      })),
    [routes, activeRouteStack]
  );

  // The deepest active route that has children defines the contextual sub-nav
  // (e.g. the open client's plan/scenarios, or a section's sub-pages).
  const currentActiveRoute = React.useMemo(
    () =>
      activeRouteStack.length > 1
        ? activeRouteStack.filter((route) => Boolean(route.subNav)).pop()
        : activeRouteStack[0],
    [activeRouteStack]
  );

  const context = React.useMemo<SideNavContext | undefined>(() => {
    const activeTop = activeRouteStack[0];
    const landingHref = activeTop ? resolveHref(activeTop) : undefined;

    const items: SideNavItem[] = (currentActiveRoute?.subNav ?? [])
      .map((route) => ({
        id: route.id,
        label: route.label,
        href: resolveHref(route),
        isActive: route.isActive,
      }))
      // Drop the entry that is literally the active section's own nav item
      // (same label and destination) so e.g. "Clients" doesn't appear twice.
      .filter(
        (item) =>
          !(
            activeTop &&
            item.label === activeTop.label &&
            item.href === landingHref
          )
      );

    // An explicit title (the open client's name) is the only thing that marks a
    // true sub-context; section landing pages carry no title. The back-link and
    // title are shown only in that case so an individual viewing their own plan
    // never sees "All clients".
    const hasExplicitTitle = Boolean(currentActiveRoute?.title);

    // Show the block only when it adds something beyond the section's own
    // landing page — otherwise it would just repeat the top-level nav item.
    const meaningful =
      hasExplicitTitle ||
      items.length >= 2 ||
      (items.length === 1 && items[0].href !== landingHref);

    if (!meaningful) return undefined;

    return {
      title: currentActiveRoute?.title,
      items,
      // Back-link comes from the active section's leaf (Clients -> "All clients",
      // Prospects -> "All prospects"), falling back to clients for safety.
      backHref: hasExplicitTitle
        ? currentActiveRoute?.backHref ?? Routes.clientsPath({ tenantSlug: tenant.slug })
        : undefined,
      backLabel: hasExplicitTitle
        ? currentActiveRoute?.backLabel ?? "All clients"
        : undefined,
    };
  }, [activeRouteStack, currentActiveRoute, tenant.slug]);

  const handleLogout = () => {
    window.location.href = Routes.logoutPath({ tenantSlug: tenant.slug });
  };

  // Reveal the page once the nav has mounted (counteracts the anti-FOUC
  // `app-wait-navigation { opacity: 0 }` rule in the application layout).
  React.useEffect(() => {
    document
      .querySelector(".appui-body")
      ?.classList.remove("app-wait-navigation");
  }, []);

  // Offset the page content to make room for the fixed sidebar. Applied only
  // here, where the nav is actually mounted, so pages that hide the nav are
  // never pushed.
  React.useEffect(() => {
    const body = document.querySelector(".appui-body");
    body?.classList.add(SIDENAV_PUSH_CLASS);
    return () => body?.classList.remove(SIDENAV_PUSH_CLASS);
  }, []);

  return (
    <SideNav
      tenant={tenant}
      currentUser={currentUser}
      items={sideBarItems}
      context={context}
      helpPath={helpPath}
      settingsPath={settingsPath}
      roles={roles}
      onRoleSelected={onRoleSelected}
      onLogout={handleLogout}
    />
  );
};

export { NavigationContext as Navigation, NavigationProps };
